// The phone app's offline session (BL-2343, ADR 066 — Offline Mode § 2): stored from the session
// SessionProvider holds, and read back offline through the typed client, whose offline transport
// answers `/api/auth/me` and `/api/tenants/user` from it.
'use client';

import { useEffect } from 'react';
import { isOfflineModeBuild } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import type { SessionBootstrapPayload, SessionUserDto } from '@auctogon/core-admin/schemas/responses/session';
import type { Tenant, UserTenant } from '@auctogon/core-admin/schemas/responses/tenant';

import { api } from '../../api-client/typed-client';
import {
  getNativeDeviceGateway,
  mayBeNativeApp,
  NativeCapabilityUnavailableError,
  OFFLINE_SESSION_RECORD_KEY,
  type NativeDeviceGateway,
  type OfflineScope,
} from '../../gateways/native-device';
import { mapMeResponse } from '../session/me-response';

/** What the session provider knows once it has resolved. */
export interface RecordableSession {
  authStatus: string;
  user: SessionUserDto | null;
  currentTenant: UserTenant | null;
  userTenants: UserTenant[];
  allTenants: Tenant[];
}

/** The gateway, when this build runs offline mode inside the native app; else null. */
async function nativeGatewayForOffline(): Promise<NativeDeviceGateway | null> {
  if (!isOfflineModeBuild(process.env.NEXT_PUBLIC_AUCTOGON_ENV)) return null;
  // A plain browser loads no adapter chunk to learn it is not the app.
  if (!mayBeNativeApp()) return null;
  const gateway = await getNativeDeviceGateway();
  return gateway.isNativeApp() ? gateway : null;
}

/**
 * Session writes and pointer changes run one at a time, in call order, and each call bumps the
 * generation. A write whose generation is no longer current is dropped, so an older write still
 * in flight can never land after a newer tenant switch or after a sign-out.
 */
let generation = 0;
let tail: Promise<void> = Promise.resolve();

function enqueue(task: () => Promise<void>): Promise<void> {
  const run = tail.then(task, task);
  // The chain survives a failed write; the caller still sees the rejection through `run`.
  tail = run.catch(() => undefined);
  return run;
}

function warn(message: string, err: unknown): void {
  // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Best effort, nothing to surface inline.
  console.warn(message, err instanceof Error ? err : new Error(String(err)));
}

/**
 * Store the session for an offline cold start and point the device at its scope. Runs offline too:
 * a tenant switch offline must move the scope the offline transport reads, or the new tenant's
 * header would sit over the old tenant's records (and, with BL-2431, queue into its scope).
 *
 * @param session - the resolved session; nothing is stored unless it is signed in with a tenant.
 * @returns once stored, or at once when there is nothing to store.
 * @sideEffect App: writes the scope's session record and the device's active-scope pointer.
 */
export async function recordOfflineSession(session: RecordableSession): Promise<void> {
  const { authStatus, user, currentTenant, userTenants, allTenants } = session;
  if (authStatus !== 'authenticated' || !user || !currentTenant) return;
  const mine = ++generation;
  const current = () => mine === generation;
  await enqueue(async () => {
    if (!current()) return;
    const gateway = await nativeGatewayForOffline();
    if (!gateway || !current()) return;
    const scope = { tenantId: currentTenant.id, userId: user.id };
    const store = await gateway.openOfflineStore(scope);
    if (!current()) return;
    await store.records.put({
      collection: 'session',
      key: OFFLINE_SESSION_RECORD_KEY,
      value: { user, tenants: { userTenants, allTenants }, syncedAt: new Date().toISOString() },
    });
    if (!current()) return;
    await gateway.setActiveOfflineScope(scope);
  });
}

/**
 * Keep the stored offline session current: on sign-in, on a tenant switch, and on a refresh of
 * the user or the tenant lists. Called by `SessionProvider`.
 *
 * @param session - the provider's resolved session.
 * @sideEffect App: see {@link recordOfflineSession}. A failure is logged and changes nothing on screen.
 */
export function useRecordOfflineSession(session: RecordableSession): void {
  const { authStatus, user, currentTenant, userTenants, allTenants } = session;
  useEffect(() => {
    recordOfflineSession({ authStatus, user, currentTenant, userTenants, allTenants }).catch((err: unknown) => {
      warn('[offline-session] could not store the offline session', err);
    });
  }, [authStatus, user, currentTenant, userTenants, allTenants]);
}

/**
 * Whether the device's active session is still this scope (ADR 066 — Offline Mode § 6.1: only the
 * active tenant and user's store is ever replayed). Waits for this page's queued session writes
 * first, so a sync starting with a sign-in or a tenant switch sees the pointer that move writes.
 * Another tab writes the same pointer, so one signing in as someone else ends this tab's replay
 * (BUG-2064).
 *
 * A store holding neither a pointer nor this scope's session record was deleted under the page
 * (a versionchange or an eviction, BUG-2065), not signed out: sign-out clears only the pointer.
 * No other session can have moved a pointer that is gone with the store, so this page's own
 * session stands and its sync re-downloads.
 *
 * @param scope - the tenant and user a sync engine and its queue belong to.
 * @param options.readScopeSession - reads this scope's stored session record through the
 *   caller's open handle (never a fresh open, which could wipe another user's store).
 * @returns true when the pointer names the scope, when the store was deleted under the page, or
 *   when this build keeps no pointer (outside the native app or an offline-mode build); false
 *   otherwise, or when the pointer cannot be read.
 */
export async function isActiveOfflineScope(
  scope: OfflineScope,
  options: { readScopeSession?: () => Promise<unknown> } = {},
): Promise<boolean> {
  await tail;
  try {
    const gateway = await nativeGatewayForOffline();
    if (!gateway) return true;
    const active = await gateway.readActiveOfflineScope();
    if (active == null) return options.readScopeSession != null && (await options.readScopeSession()) == null;
    return active.tenantId === scope.tenantId && active.userId === scope.userId;
  } catch (err) {
    warn('[offline-session] could not read the active offline scope', err);
    return false;
  }
}

/**
 * Clear the device's active scope on sign-out, so an offline cold start shows the signed-out
 * state instead of reopening this session. Wipes no data (ADR 066 — Offline Mode § 6.1 limit 3).
 *
 * @returns once cleared; never rejects.
 * @sideEffect App: deletes the active-scope pointer.
 */
export async function clearActiveOfflineScope(): Promise<void> {
  // A newer generation drops every session write still queued or in flight.
  ++generation;
  try {
    await enqueue(async () => {
      const gateway = await nativeGatewayForOffline();
      await gateway?.setActiveOfflineScope(null);
    });
  } catch (err) {
    warn('[offline-session] could not clear the offline session on sign-out', err);
  }
}

/**
 * Build `SessionProvider`'s session offline from `GET /api/auth/me` and `GET /api/tenants/user`,
 * which the offline transport answers from the stored copy (AC 10). The active tenant is the
 * device's active scope.
 *
 * @returns the session; never rejects. `unauthenticated` when nothing is stored or the device
 *   was signed out; `error` when the store could not be read.
 */
export async function loadOfflineSession(): Promise<SessionBootstrapPayload> {
  try {
    const meResponse = await api.auth.me.get.raw();
    if (meResponse.status === 401) return { status: 'unauthenticated' };
    if (!meResponse.ok) return { status: 'error' };
    const user = mapMeResponse(await meResponse.json());
    if (!user) return { status: 'unauthenticated' };
    const tenants = await api.tenants.user.get();
    if (!tenants.success) return { status: 'error' };
    const scope = await (await getNativeDeviceGateway()).readActiveOfflineScope();
    const active = tenants.data.userTenants.find((tenant) => tenant.id === scope?.tenantId) ?? null;
    return {
      status: 'authenticated',
      user,
      activeTenantId: active?.id ?? null,
      userTenants: tenants.data.userTenants,
      allTenants: tenants.data.allTenants,
    };
  } catch (err) {
    if (err instanceof NativeCapabilityUnavailableError) return { status: 'unauthenticated' };
    // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). The shell shows the error state.
    console.error('[offline-session] could not read the offline session', err instanceof Error ? err : new Error(String(err)));
    return { status: 'error' };
  }
}
